import { useEffect, useState } from 'react';
import {
  canAttemptRefresh,
  clearAuth,
  getAuthToken,
  hasValidAuthToken,
  isSessionTerminallyDead,
  refreshAccessToken,
  resolveBootstrapAction,
  resolveBootstrapOutcome,
  startTokenRefresh
} from 'utils/auth';

type SplashControls = {
  showSplash: (reason: string) => void;
  hideSplash: (reason: string) => void;
};

/**
 * COLD-START SESSION RECOVERY. An expired session token is not the same thing
 * as an ended session. On a tenant with a 1-day session token and a 30-day
 * refresh token, every player returning the next day arrives here with a dead
 * session token and a perfectly good refresh token — precisely the state
 * Descope's refresh flow exists to serve.
 *
 * This effect used to read `if (!hasValidAuthToken()) clearAuth()`. Because
 * the refresh token shares one cookie/localStorage blob with the session
 * token, that deleted the refresh token before anything could spend it, and
 * App's scheduler tail then called stopTokenRefresh() since `isGuest` was
 * already true. Net effect: on a cold start the client never called Descope's
 * refresh endpoint even once, so the effective session length was the SESSION
 * token's TTL and the 30-day refresh token was dead weight — a forced login
 * every single day.
 *
 * EN-10549's Fix 19 (session-end verify-before-teardown) does not cover this:
 * it sits behind `isSessionEndTransition(wasMember, isGuest)`, and on a cold
 * start `wasMemberRef` initializes to `false`, so the transition never fires.
 *
 * Lives in its own hook rather than inline in App.tsx because App has no test
 * harness -- 2,000+ lines behind the whole provider stack -- and that
 * untestability is precisely why this hole survived the EN-10549 fix chain:
 * every individual piece was correct and tested in isolation while their
 * COMPOSITION at mount was wrong and tested nowhere. As a hook, the mount
 * effect, its splash cover and both cleanups are reachable from renderHook.
 *
 * Returns whether the bootstrap refresh is still on the wire.
 */
export const useBootstrapSessionRecovery = ({
  showSplash,
  hideSplash
}: SplashControls): boolean => {
  const [bootstrapRefreshing, setBootstrapRefreshing] = useState(
    () =>
      resolveBootstrapAction({
        hasValidToken: hasValidAuthToken(),
        canRefresh: canAttemptRefresh(),
        hasStoredSession: !!getAuthToken().refresh_token
      }).kind === 'refresh'
  );
  useEffect(() => {
    const action = resolveBootstrapAction({
      hasValidToken: hasValidAuthToken(),
      canRefresh: canAttemptRefresh(),
      hasStoredSession: !!getAuthToken().refresh_token
    });

    if (action.kind === 'ok') return;

    if (action.kind === 'clear') {
      // Nothing left to refresh with. Clear the FULL member state, not just the
      // token: clearing only the cookie left the previous member's playerGUID /
      // name / subscriptions in localStorage, which guest-reachable code reads —
      // the same identity-retention bug the explicit-logout path fixes. Delegate
      // to clearAuth() so all clear paths share one authoritative key list.
      console.warn('[Auth] bootstrap clear', { reason: action.reason });
      clearAuth();
      return;
    }

    // action.kind === 'refresh' — spend the refresh token before writing this
    // visitor off as a guest.
    //
    // Deliberately NOT gated behind a synchronous "assume still a member" latch
    // that would keep hasValidAuthToken() true across the round trip. Such a
    // latch makes `wasMemberRef` initialize to `true`, so a transient failure
    // would trip the session-end effect into clearAuth() + hardReload() — and a
    // cold start with the radio still coming up is exactly when a transient
    // failure is likeliest, so the reward for holding a good refresh token
    // would be a hard reload with no network behind it. Staying a guest for the
    // round trip and switching to member in place on success is the recoverable
    // ordering; the splash effect below covers the window visually.
    console.warn('[Auth] bootstrap — session token expired, refresh token still valid; refreshing');
    refreshAccessToken()
      .then((succeeded) => {
        const outcome = resolveBootstrapOutcome({
          succeeded,
          terminallyDead: isSessionTerminallyDead(),
          hasValidToken: hasValidAuthToken()
        });
        if (outcome.kind === 'recovered') {
          // storeAuthTokens() has already fired notifyAuthChange(), so useGuest
          // flips to member and the scheduler is anchored to the new token.
          console.warn('[Auth] bootstrap refresh succeeded — session recovered');
          return;
        }
        if (outcome.kind === 'teardown') {
          console.warn('[Auth] bootstrap refresh failed terminally — clearing session');
          clearAuth();
          return;
        }
        // Transient failure. Keep the refresh token and let the backoff chain
        // retry: the player stays a guest until an attempt lands, but nothing is
        // destroyed, so a later retry — or simply the next launch on a working
        // network — still recovers the session.
        console.warn('[Auth] bootstrap refresh failed non-terminally — scheduling retry');
        startTokenRefresh();
      })
      .finally(() => setBootstrapRefreshing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cover the refresh round trip. The bootstrap effect above deliberately
  // leaves the app in guest state while the refresh
  // is on the wire (see its comment for why a "still a member" latch is the
  // wrong trade), so without this the player watches the guest lobby paint and
  // then swap to the member lobby a moment later — correct, but it reads as a
  // flicker on every daily return. Reasons are tracked in a map, so this and
  // the native-login cover in App can overlap without either hiding the other's.
  useEffect(() => {
    if (!bootstrapRefreshing) return undefined;
    showSplash('authBootstrap');
    return () => hideSplash('authBootstrap');
  }, [bootstrapRefreshing, showSplash, hideSplash]);

  return bootstrapRefreshing;
};

export default useBootstrapSessionRecovery;
